import React, { useMemo } from 'react'
import {
  Card,
  CardHeader,
  CardActionArea,
  Avatar,
  Typography,
  Box,
  CardContent,
} from '@material-ui/core'
import { track } from 'utils/segmentTracking'
import { makeStyles } from '@material-ui/core/styles'
import LogoImage from 'assets/images/coachbetter-ball.svg'
import GooglePlayLogo from 'assets/images/logos/google_play.svg'
import AppStoreLogo from 'assets/images/logos/app_store_black.svg'
import PropTypes from 'prop-types'
import { Translate } from 'react-localize-redux'
import { useHistory } from 'react-router-dom'
import { CLUB_SUBSCRIPTION_ROUTE } from 'constants/routes'

const useStyles = makeStyles((theme) => ({
  teamCard: {
    border: `1px solid ${theme.palette.primary.main}`,
    borderRadius: '12px',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
    '& a:hover': {
      color: theme.palette.primary.main,
    },
    position: 'relative',
  },
  cardHeader: {
    display: 'flex',
    flexDirection: 'column',
    paddingTop: '1.5rem',
    '& .MuiCardHeader-avatar': {
      marginRight: '0',
    },
    '& .MuiAvatar-root': {
      height: '100px',
      width: '100px',
      marginBottom: '.5rem',
      marginRight: '0!important',
    },
  },
  teamCardContent: {
    display: 'flex',
    flexDirection: 'row',
    padding: '.5rem 1.5rem 1.5rem 1.5rem',
    justifyContent: 'space-between',
  },
  teamInfoItem: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    margin: '.2rem',
  },
  typographyColor: {
    color: 'black',
  },
  appStoreWrapper: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: '1rem',
  },
  appStoreLink: {
    marginRight: theme.spacing(1),
  },
  appImg: {
    height: '40px',
    width: 'auto',
  },
}))

const TeamListItem = ({ team, user, clubTheme, selectedClub }) => {
  const classes = useStyles(clubTheme)
  const history = useHistory()
  const latestSeasonIdOfTeam = useMemo(() => {
    const d = new Date(
      new Date().getFullYear(),
      new Date().getMonth(),
      new Date().getDate()
    )
    const activeSeason = team.relationships?.seasons.filter(
      (season) =>
        d >= new Date(season.start_date) && d <= new Date(season.end_date)
    )
    const latestSeason =
      activeSeason.length > 0
        ? activeSeason[0]
        : team.relationships?.seasons[team.relationships?.seasons.length - 1]

    return latestSeason?.id
  }, [team.relationships?.seasons])

  const userCanAccessTeam = team.can.some(
    (permission) => permission === 'access-team-via-web-app'
  )

  const handleTeamClick = (e) => {
    e.preventDefault()
    e.stopPropagation()

    if (!userCanAccessTeam) {
      return
    }

    const hasValidSubscription = selectedClub.subscriptions.some((subscription) => subscription.valid)

    if (!hasValidSubscription) {
      history.push(
        CLUB_SUBSCRIPTION_ROUTE.populateURLParams({
          clubUuid: selectedClub.uuid,
        })
      )

      return
    }

    // Segment tracking
    track('WClick_SelectTeam', {
      category: 'select team',
      userUuid: user.uuid,
      userEmail: user.email,
      teamName: team.name,
    })

    // @todo: change it to season uuid as id field will be deprecated
    history.push(`/team/${team.uuid}/season/${latestSeasonIdOfTeam}`)
  }

  return (
    <Box>
      <Card className={classes.teamCard} onClick={handleTeamClick}>
        <CardActionArea>
          <CardHeader
            className={classes.cardHeader}
            avatar={
              <Avatar
                src={team.relationships.logo?.full_url || LogoImage}
                alt={team.name}
              />
            }
            title={<Typography variant="h4">{team.name}</Typography>}
          />
          <CardContent className={classes.teamCardContent}>
            <Box className={classes.teamInfoItem}>
              <Typography
                noWrap
                variant="body2"
                color="textSecondary"
                component="p"
              >
                <Translate id="myteams.team-id" />
              </Typography>
              <Typography
                variant="body2"
                color="textSecondary"
                className={classes.typographyColor}
                noWrap
              >
                {team.id}
              </Typography>
            </Box>
            <Box className={classes.teamInfoItem}>
              <Typography
                noWrap
                variant="body2"
                color="textSecondary"
                component="p"
              >
                <Translate id="myteams.age-group" />
              </Typography>
              <Typography
                variant="body2"
                color="textSecondary"
                className={classes.typographyColor}
                noWrap
              >
                {team.relationships?.age_class?.name}
              </Typography>
              {selectedClub && selectedClub.enable_team_tags && (
                <Typography
                  variant="body2"
                  color="textSecondary"
                  className={classes.typographyColor}
                  noWrap
                >
                  {team.relationships?.team_tag?.name || <span>&nbsp;</span>}
                </Typography>
              )}
            </Box>
            <Box className={classes.teamInfoItem}>
              <Typography
                noWrap
                variant="body2"
                color="textSecondary"
                component="p"
              >
                <Translate id="drills.players" />
              </Typography>
              <Typography
                variant="body2"
                color="textSecondary"
                className={classes.typographyColor}
                noWrap
              >
                {team.active_players_count}
              </Typography>
            </Box>
          </CardContent>
        </CardActionArea>
        {!userCanAccessTeam && (
          <div className={classes.appStoreWrapper}>
            <a
              className={classes.appStoreLink}
              href={selectedClub.apps.ios}
            >
              <img
                className={classes.appImg}
                src={AppStoreLogo}
                alt="Download on the App Store"
              />
            </a>
            <a href={selectedClub.apps.android}>
              <img
                className={classes.appImg}
                src={GooglePlayLogo}
                alt="Get it on GooglePlay"
              />
            </a>
          </div>
        )}
      </Card>
    </Box>
  )
}

TeamListItem.defaultProps = {
  selectedClub: {
    enable_team_tags: false,
  },
  clubTheme: {
    primarycolor: '#000',
    buttoncolor: '#000',
  },
}

TeamListItem.propTypes = {
  team: PropTypes.shape({
    id: PropTypes.number.isRequired,
    uuid: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    can: PropTypes.arrayOf(PropTypes.string),
    active_players_count: PropTypes.number.isRequired,
    relationships: PropTypes.shape({
      logo: PropTypes.shape({
        full_url: PropTypes.string.isRequired,
      }),
      seasons: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.number.isRequired,
          start_date: PropTypes.string.isRequired,
          end_date: PropTypes.string.isRequired,
        })
      ),
      age_class: PropTypes.shape({
        name: PropTypes.string,
      }),
      team_tag: PropTypes.shape({
        name: PropTypes.string,
      }),
    }).isRequired,
  }).isRequired,

  clubTheme: PropTypes.shape({
    primarycolor: PropTypes.string.isRequired,
    buttoncolor: PropTypes.string.isRequired,
  }),
  selectedClub: PropTypes.shape({
    uuid: PropTypes.string.isRequired,
    enable_team_tags: PropTypes.bool.isRequired,
    subscriptions: PropTypes.arrayOf(PropTypes.shape()),
    apps: PropTypes.shape({
      ios: PropTypes.string,
      android: PropTypes.string,
    }),
  }),
  user: PropTypes.shape({
    uuid: PropTypes.string,
    email: PropTypes.string,
  }).isRequired,
}

export default TeamListItem
